import React, { useEffect, useState, useRef, useContext } from 'react';
import styled from 'styled-components';
import {
  HrBlockContainer,
  HrImageContainer,
  HrCustomButton,
  HrPrimary,
  HrWhite,
  HrGreenHover,
  HrlighterGrey,
  HrFlexContainer,
  HrLabel,
  HrParagraph,
  HrRed
} from '@components/styles/hr-styles';
import { Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { get, isString } from 'lodash';
import { ErrorMessage } from '@hookform/error-message';
import { PropTypes } from 'prop-types';
import { DisplaySettingsContext } from '../store/display-settings';

const Container = styled(HrBlockContainer)`
  position: relative;
  height: 150px;
  width: 150px;

  & div.error {
    border: 1px solid ${HrRed};
  }
`;
const Button = styled(HrCustomButton)`
  position: absolute;
  right: 0px;
  bottom: 0px;
  width: 40px;
  height: 40px;
  background: ${HrPrimary};
  border-radius: 50%;

  &:hover {
    background-color: ${HrGreenHover};
  }

  & span {
    color: ${HrWhite};
    font-size: 24px;
  }
`;

export const UploadImage = ({ fotoUrl, control, validate, formState, trigger, name, setValue }) => {
  const { errors } = formState;
  const errorUpload = get(errors, 'foto_perfil');
  const [t] = useTranslation();
  const fileInputRef = useRef();
  const refCurrent = useRef(null);
  const [image, setImage] = useState();
  const [preview, setPreview] = useState({
    url: '',
    file: null
  });
  const _validate = {};

  if (validate.required) {
    _validate.required = 'Debes adjuntar una foto ya que es un campo obligatorio.';
  }
  if (validate.size) {
    _validate.validate = {
      size: (value) => {
        if (!isString(value) && value && value.size > 5242880) {
          return 'El tamaño máximo es de 5MB';
        }
        return true;
      }
    };
    // 'El archivo no puede ser mayor a 15MB';
  }
  if (validate.format) {
    _validate.validate = {
      ...(_validate.validate || {}),
      formatter: (value) => {
        if (!isString(value) && value) {
          const nameValid = value.name.toLowerCase();
          const validFormats = ['jpg', 'jpeg', 'png', 'webp'];
          const fileExtension = nameValid.split('.').pop();
          if (!validFormats.includes(fileExtension)) {
            return `El formato ${fileExtension} es invalido`;
          }

          return true;
        }
      }
    };
  }

  useEffect(() => {
    if (errorUpload) {
      refCurrent.current?.scrollIntoView({ block: 'start', behavior: 'smooth' });
      refCurrent.current?.focus();
      setImage(null);
    }
  }, [errorUpload]);

  useEffect(() => {
    if (fotoUrl) {
      setValue(name, fotoUrl);
      setPreview((prev) => ({ ...prev, url: fotoUrl }));
    }

    if (image) {
      setPreview((prev) => ({ ...prev, url: null, file: URL.createObjectURL(image) }));
      setValue(name, image);
    }

    if (!fotoUrl && !image) {
      setPreview((prev) => ({ ...prev, url: null, file: null }));
      setValue(name, null);
    }
  }, [image, fotoUrl]);

  const handleUploadImage = async (event) => {
    const isValid = await trigger(name);
    if (isValid) {
      setImage(event);
    }
  };

  const { darkMode, fontSizeSetting } = useContext(DisplaySettingsContext);

  return (
    <HrFlexContainer
      flexDirection='column'
      alignItems='flex-start'
      gap='8px'
      minWidth='150px'
      width='auto'
      ref={refCurrent}
    >
      <HrLabel fontSizeSetting={fontSizeSetting} isRequired={validate.required} userSelect='none'  color={darkMode ? 'white' : ''}>
        {t('postulant.fotoURL')}
      </HrLabel>
      <Container>
        {preview.url || preview.file ? (
          <HrImageContainer width='150px' height='150px' borderRadius='50%' className={errors[name] ? 'error' : ''}>
            <img src={preview.url || preview.file} alt='foto de perfil' />
          </HrImageContainer>
        ) : (
          <HrFlexContainer
            backgroundColor={HrlighterGrey}
            width='150px'
            height='150px'
            borderRadius='50%'
            className={errors[name] ? 'error' : ''}
          >
            <HrParagraph fontSizeSetting={fontSizeSetting} padding='0 4px' textAlign='center'>
              {t('postulant.foto_legend')}
            </HrParagraph>
          </HrFlexContainer>
        )}
        <Button
          type='button'
          onClick={(event) => {
            event.preventDefault();
            fileInputRef.current.click();
          }}
        >
          <span className='hr-Plus' />
        </Button>
        <Controller
          name={name}
          control={control}
          rules={_validate}
          render={(props) => {
            return (
              <input
                type='file'
                ref={fileInputRef}
                accept='.jpg, .jpeg, .png, .webp'
                onChange={(event) => {
                  props.field.onChange(event.target.files[0]);
                  handleUploadImage(event.target.files[0]);
                }}
                style={{ display: 'none' }}
              />
            );
          }}
        />
      </Container>
      {errors.foto_perfil && (
        <ErrorMessage
          errors={errors}
          name='foto_perfil'
          render={({ message }) => <HrParagraph color={HrRed}>{message}</HrParagraph>}
        />
      )}
    </HrFlexContainer>
  );
};

UploadImage.propTypes = {
  // TODO:revisar caso de uso con el tipo number y value
  name: PropTypes.string,
  value: PropTypes.string,
  validate: PropTypes.object,
  errorPosition: PropTypes.oneOf(['top', 'bottom']),
  accept: PropTypes.string
};

UploadImage.defaultProps = {
  name: '',
  value: '',
  validate: {},
  errorPosition: 'top',
  accept: '.pdf, .doc, .docx'
};
